/**
 * Design Tokens & Theme System
 * Centralized configuration for colors, typography, spacing, and component styles
 * Ensures consistency across the entire application
 */

export const designTokens = {
  // Color Palette
  colors: {
    // Primary colors
    primary: {
      main: "#2ca58d",
      light: "#3ab399",
      dark: "#1f8671",
      lighter: "#e8f5f1",
    },
    // Accent colors (Gold/Amber)
    accent: {
      main: "#cc9900",
      light: "#ffd866",
      dark: "#b58600",
      lighter: "#fffbe7",
    },
    // Neutral colors
    neutral: {
      white: "#ffffff",
      offWhite: "#f8fafc",
      light: "#f5f5f5",
      medium: "#999999",
      dark: "#333333",
      darker: "#131618",
    },
    // Semantic colors
    semantic: {
      success: "#22c55e",
      warning: "#f59e0b",
      error: "#ef4444",
      info: "#3b82f6",
    },
  },

  // Typography
  typography: {
    fontFamily: {
      primary: "'Roboto', sans-serif",
      fallback: "system-ui, -apple-system, sans-serif",
    },
    fontSize: {
      // Page headings (H1)
      h1: {
        desktop: "48px",
        tablet: "36px",
        mobile: "28px",
        weight: 700,
        lineHeight: 1.2,
      },
      // Section headings (H2)
      h2: {
        desktop: "40px",
        tablet: "32px",
        mobile: "24px",
        weight: 700,
        lineHeight: 1.25,
      },
      // Card/subsection titles (H3)
      h3: {
        desktop: "28px",
        tablet: "24px",
        mobile: "20px",
        weight: 600,
        lineHeight: 1.3,
      },
      // Body text
      body: {
        large: {
          size: "18px",
          weight: 400,
          lineHeight: 1.6,
        },
        normal: {
          size: "16px",
          weight: 400,
          lineHeight: 1.6,
        },
        small: {
          size: "14px",
          weight: 400,
          lineHeight: 1.5,
        },
      },
      // Labels and metadata
      label: {
        size: "12px",
        weight: 500,
        lineHeight: 1.4,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      },
      // Button text
      button: {
        size: "16px",
        weight: 600,
        lineHeight: 1.5,
      },
    },
  },

  // Spacing System (8px base unit)
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    xxl: "48px",
    xxxl: "64px",
  },

  // Border Radius
  borderRadius: {
    none: "0",
    small: "4px",
    medium: "8px",
    large: "12px",
    xlarge: "16px",
    round: "50%",
  },

  // Shadows
  shadows: {
    none: "none",
    xs: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    sm: "0 2px 8px 0 rgba(0, 0, 0, 0.08)",
    md: "0 4px 12px 0 rgba(0, 0, 0, 0.1)",
    lg: "0 8px 24px 0 rgba(0, 0, 0, 0.12)",
    xl: "0 12px 40px 0 rgba(204, 153, 0, 0.15)",
    card: "0 6px 32px rgba(204, 153, 0, 0.15), 0 2px 12px rgba(0, 0, 0, 0.06)",
  },

  // Transitions & Animations
  transitions: {
    fast: "0.15s ease-in-out",
    normal: "0.3s ease-in-out",
    smooth: "0.6s cubic-bezier(0.64, 0, 0.78, 0)",
  },

  // Responsive breakpoints
  breakpoints: {
    mobile: "576px",
    tablet: "768px",
    desktop: "992px",
    wide: "1200px",
    ultraWide: "1440px",
  },

  // Z-index scale
  zIndex: {
    hide: -1,
    base: 0,
    dropdown: 100,
    sticky: 200,
    fixed: 500,
    modal: 1000,
    tooltip: 1100,
    notification: 1200,
  },

  // Component-specific tokens
  components: {
    button: {
      padding: {
        small: "8px 16px",
        medium: "12px 24px",
        large: "16px 32px",
      },
      height: {
        small: "32px",
        medium: "40px",
        large: "48px",
      },
    },
    card: {
      padding: "20px",
      borderRadius: "12px",
      shadow: "0 6px 32px rgba(204, 153, 0, 0.15)",
    },
    input: {
      padding: "12px 16px",
      borderRadius: "8px",
      height: "40px",
    },
    propertyCard: {
      minHeight: "420px",
      imageHeight: "220px",
      borderRadius: "22px",
    },
  },
};

/**
 * Helper function to generate responsive font sizes
 * Usage: responsiveFontSize(designTokens.typography.fontSize.h2)
 */
export const responsiveFontSize = (fontConfig) => `
  font-size: ${fontConfig.desktop};
  font-weight: ${fontConfig.weight};
  line-height: ${fontConfig.lineHeight};

  @media (max-width: 992px) {
    font-size: ${fontConfig.tablet};
  }

  @media (max-width: 768px) {
    font-size: ${fontConfig.mobile || fontConfig.tablet};
  }
`;

/**
 * Helper function for media queries
 * Usage: media.tablet`...css...`
 */
export const media = {
  mobile: (styles) => `@media (max-width: 576px) { ${styles} }`,
  tablet: (styles) => `@media (max-width: 768px) { ${styles} }`,
  desktop: (styles) => `@media (max-width: 992px) { ${styles} }`,
  wide: (styles) => `@media (min-width: 1200px) { ${styles} }`,
};

/**
 * Mixin styles for common component patterns
 */
export const mixins = {
  flexCenter: `
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  flexBetween: `
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  truncate: `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  textClamp: (lines = 2) => `
    display: -webkit-box;
    -webkit-line-clamp: ${lines};
    -webkit-box-orient: vertical;
    overflow: hidden;
  `,
};
